import type { LearnosityRequest } from '../models/LearnosityRequest';
import { isAbortError, throwIfAborted } from '../utils/fetchCancellation';

export type LearnosityFetchOperation =
  | 'init_object'
  | 'get_item_references'
  | 'assessment_status'
  | 'get_incomplete_status';

export class LearnosityFetchError extends Error {
  constructor(
    public readonly operation: LearnosityFetchOperation,
    public readonly category: 'http' | 'invalid_payload',
    public readonly status: number,
  ) {
    super(`Learnosity ${operation} ${category} failure (HTTP ${status})`);
    this.name = 'LearnosityFetchError';
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonemptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function isLearnosityRequest(value: unknown): value is LearnosityRequest {
  if (!isObject(value) || !isObject(value.security) || !isObject(value.request)) return false;
  const { security, request } = value;
  return (
    ['consumer_key', 'domain', 'timestamp', 'user_id', 'signature'].every(field => isNonemptyString(security[field])) &&
    ['user_id', 'session_id', 'activity_id', 'rendering_type', 'type'].every(field =>
      isNonemptyString(request[field]),
    ) &&
    typeof request.name === 'string' &&
    (request.state === undefined || typeof request.state === 'string') &&
    (request.activity_template_id === undefined || typeof request.activity_template_id === 'string')
  );
}

export function isItemReferences(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isNonemptyString);
}

export async function readLearnosityResponse<T>(
  response: Response,
  operation: LearnosityFetchOperation,
  validate: (value: unknown) => value is T,
  signal?: AbortSignal,
): Promise<T> {
  if (!response.ok) throw new LearnosityFetchError(operation, 'http', response.status);
  let data: unknown;
  try {
    data = await response.json();
  } catch (error) {
    throwIfAborted(signal);
    if (isAbortError(error)) throw error;
    throw new LearnosityFetchError(operation, 'invalid_payload', response.status);
  }
  throwIfAborted(signal);
  if (!validate(data)) throw new LearnosityFetchError(operation, 'invalid_payload', response.status);
  return data;
}
